'use client'

/** The header's story clock pill, search button and Needs-you bell. */

import React from 'react'
import Link from 'next/link'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/ui/icons'
import { ElapsedTime } from '@/components/shell'
import { openCommandPalette } from '@/components/CommandPalette'
import { useNavBadges } from '@/hooks/useShellData'
import { useStory } from '@/lib/story'

/** "EQ-2026-A · +16 h 20 min", or "Before the earthquake" (plans/03 A5.2). Only the Presenter moves it. */
export function StoryClockPill() {
  const { story, elapsedLabel } = useStory()
  if (!story) return null
  return (
    <span aria-live='polite' aria-atomic='true' className='inline-flex'>
      <span className='sr-only'>Story clock: </span>
      {/* Below xl the time only, so the page crumb and the story chip keep their room; xl adds the event id. */}
      <ElapsedTime min={story.clock_min} variant='pill' label={elapsedLabel} title={story.step_label} className='xl:hidden' />
      <ElapsedTime
        min={story.clock_min}
        variant='pill'
        label={elapsedLabel}
        prefix={story.event_id}
        title={story.step_label}
        className='hidden xl:inline-flex'
      />
    </span>
  )
}

/** "Ctrl K", or "⌘K" on a Mac (read after mount, so the server and first client render agree). */
function useShortcutLabel(): string {
  const [label, setLabel] = React.useState('Ctrl K')
  React.useEffect(() => {
    if (/Mac|iPhone|iPad/i.test(navigator.userAgent)) setLabel('⌘K')
  }, [])
  return label
}

export function SearchButton() {
  const shortcut = useShortcutLabel()
  return (
    <button
      type='button'
      onClick={openCommandPalette}
      aria-keyshortcuts='Control+K Meta+K'
      className={cn(
        'group flex h-9 w-60 items-center gap-2 px-3',
        'rounded-xl border border-border bg-card',
        'text-sm text-muted-foreground',
        'transition-colors duration-200 hover:border-brand-cornflower',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <Icons.search className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
      <span className='flex-1 truncate text-left'>Search pages and records</span>
      <kbd className='hidden h-5 items-center rounded bg-muted px-1.5 font-mono text-xs text-muted-foreground sm:inline-flex'>
        {shortcut}
      </kbd>
    </button>
  )
}

/** Needs-you bell: a dot while a decision waits on this persona; opens the persona's approvals. */
export function NeedsYouBell() {
  const badges = useNavBadges()
  const waiting = (badges.workbench ?? 0) > 0
  return (
    <Link
      href='/workbench?tab=approvals&queue=mine'
      aria-label={waiting ? 'Needs you: decisions are waiting' : 'Needs you: nothing waiting'}
      title='Needs you'
      className={cn(
        'relative flex h-9 w-9 items-center justify-center rounded-xl border border-border bg-card text-foreground',
        'transition-colors hover:border-brand-cornflower',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
      )}
    >
      <Icons.bell className='h-4 w-4' strokeWidth={1.5} aria-hidden='true' />
      {waiting && (
        <span
          aria-hidden='true'
          className='absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-needs-you ring-2 ring-card'
        />
      )}
    </Link>
  )
}
